// Direct-Sale Invoices — a "New Invoice" typed up with no quotation/proforma
// behind it (e.g. a walk-in/cash sale). It is a SalesOrder with documentType
// 'direct_sale' (see db-sales.js and useInvoices.ts) — it already carries a
// real document number (DS/…) the moment it is created, but it is not yet a
// tax invoice: like Quotations and Proforma, "Create Tax Invoice" raises it
// as an Invoice Request and generates the real, ledger-posted invoice from
// it in the same click (src/api-invoice-requests).

import { useEffect, useMemo, useState } from 'react';
import { DispatchStatusBadge, type DispatchStatusInfo } from '@/shared/components/DispatchStatusBadge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Receipt, ReceiptText, Plus, Trash2, Wallet, Clock, Eye, Download, ExternalLink, X, Search, Send, ChevronLeft, ChevronRight, ArrowRight, Pencil, CheckCircle2, Ban } from 'lucide-react';
import { DocumentChainActions } from '@/shared/components/documents/DocumentChainActions';
import { DocumentActions } from '@/shared/components/documents/DocumentActions';
import { useSendTaxInvoice, describeSendResult, sendErrorText } from '@/shared/hooks/useDocumentSend';
import { useDocumentChain, asAdvanceBlocked, asOverrideReasonRequired, type AdvanceBlocked } from '@/shared/hooks/useDocumentChain';
import { cn, localToday } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';
import { baseUnitDivisor, packSummary, piecesPerPack, ratePerBaseUnit, totalBaseQuantityLabel } from '@/shared/utils/ratePerBaseUnit';
import { abbreviatePackaging, derivePackagingType } from '@/shared/utils/packagingAbbreviation';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useRememberedState } from '@/shared/hooks/useRememberedFilters';
import {
  useInvoiceList,
  useInvoiceMutations,
  useTaxInvoiceList,
  useInvoiceClients,
  type InvoiceDraft,
  type InvoiceLine,
  type TaxInvoiceRow,
  type InvoiceSort,
} from '../hooks/useInvoices';

const PAGE_SIZE = 50;

const SORT_OPTIONS: { value: InvoiceSort; label: string }[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'invoice_desc', label: 'Invoice no. (latest first)' },
  { value: 'invoice_asc', label: 'Invoice no. (oldest first)' },
  { value: 'customer_asc', label: 'Client (A-Z)' },
  { value: 'customer_desc', label: 'Client (Z-A)' },
  { value: 'amount_desc', label: 'Amount (high-low)' },
  { value: 'amount_asc', label: 'Amount (low-high)' },
];

/** Comparator matching the chosen sort key — applied to the already-unified
 *  InvoiceRow[] so the merge of two independently-sorted paginated sources
 *  (direct-sale drafts + tax invoice requests) still lands in one true order. */
/** The real invoice number, or '' for a draft / request not finalised yet. */
const billNo = (r: InvoiceRow) => (r.kind === 'tax' && r.finalized ? String(r.number || '') : '');

function compareRows(sort: InvoiceSort) {
  return (a: InvoiceRow, b: InvoiceRow) => {
    switch (sort) {
      case 'invoice_desc':
      case 'invoice_asc': {
        // Drafts (no number yet) always after the numbered invoices.
        const x = billNo(a); const y = billNo(b);
        if (!x || !y) return (x ? -1 : 0) - (y ? -1 : 0) || new Date(b.date).getTime() - new Date(a.date).getTime();
        const c = x.localeCompare(y, 'en', { numeric: true });
        return sort === 'invoice_desc' ? -c : c;
      }
      case 'date_asc':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'amount_desc':
        return b.total - a.total;
      case 'amount_asc':
        return a.total - b.total;
      case 'customer_asc':
        return a.clientName.localeCompare(b.clientName);
      case 'customer_desc':
        return b.clientName.localeCompare(a.clientName);
      case 'date_desc':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  };
}

/** One row in the unified list — either a direct-sale draft (SalesOrderModel,
 *  documentType 'direct_sale') or a real, ledger-posted Tax Invoice generated
 *  from a Quotation/Proforma (AccountsSalesInvoiceModel, read via invoice_requests). */
interface InvoiceRow {
  kind: 'draft' | 'tax';
  key: string;
  number: string;
  date: string;
  clientName: string;
  itemCount: number;
  total: number;
  status: string;
  /** id to pass to the PDF preview route — draft uses its own _id, tax uses invoiceId. */
  previewId: string;
  draft?: InvoiceDraft;
  tax?: TaxInvoiceRow;
  /** tax rows only — true once the invoice has a GST number and is posted. */
  finalized?: boolean;
  dispatchStatus?: DispatchStatusInfo;
}

function renderDispatchBadge(ds?: InvoiceRow['dispatchStatus']) {
  return <DispatchStatusBadge status={ds} />;
}

const getDocUrl = (row: InvoiceRow, format?: 'pdf') => {
  const base = row.kind === 'tax'
    ? `/api/documents/invoice/${row.previewId}`
    : `/api/documents/quotation/${row.previewId}`;
  return format === 'pdf' ? `${base}?format=pdf&download=1` : base;
};

interface DraftLine extends InvoiceLine {
  key: string;
  name: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
}

const emptyLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  productId: '',
  name: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  discountType: 'percent',
  cgstRate: 0,
  sgstRate: 0,
  igstRate: 0,
});

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function InvoicesPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTaxInvoice, setEditingTaxInvoice] = useState(false);
  const isFormOpen = creating || editingId !== null;
  const [previewRow, setPreviewRow] = useState<InvoiceRow | null>(null);

  // Chromium's Print/Save-as-PDF dialog names the file after the TOP window's
  // title, not the iframe's — override the tab title while the preview is
  // open so Save-as-PDF picks up the real invoice number instead of
  // "RBM Oil — by Zynx" (index.html).
  useEffect(() => {
    if (!previewRow) return;
    const original = document.title;
    document.title = previewRow.number || 'Invoice';
    return () => { document.title = original; };
  }, [previewRow]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  // The stored rate is always for one sale unit/container.  This only changes
  // how a rep enters it: e.g. ₹100/L for a 210 LTR drum is stored as ₹21,000.
  const [rateModeByKey, setRateModeByKey] = useState<Record<string, 'container' | 'piece' | 'base'>>({});
  // Filters stay set until reset (client, 2026-10-03) — opening an invoice and
  // coming back, another menu, a refresh: they are still applied.
  const [searchInput, setSearchInput] = useRememberedState('sales-invoices', 'search', '');
  const [searchQuery, setSearchQuery] = useState(() => searchInput.trim());
  const [listKind, setListKind] = useRememberedState<'all' | 'draft' | 'tax'>('sales-invoices', 'listKind', 'all');
  const [sort, setSort] = useRememberedState<InvoiceSort>('sales-invoices', 'sort', 'date_desc');
  const [page, setPage] = useState(1);
  const [productFilter, setProductFilter] = useRememberedState('sales-invoices', 'product', '');
  // Date-wise filter (YYYY-MM-DD, whole Indian days). The cards above the list
  // (count, value, bill numbers) follow it.
  const [fromDate, setFromDate] = useRememberedState('sales-invoices', 'fromDate', '');
  const [toDate, setToDate] = useRememberedState('sales-invoices', 'toDate', '');
  const [datePreset, setDatePreset] = useRememberedState('sales-invoices', 'datePreset', '');
  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    const d = (y: number, m: number, day: number) => localToday(new Date(y, m, day));
    const y = now.getFullYear(), m = now.getMonth();
    const fyStart = m >= 3 ? y : y - 1;
    const ranges: Record<string, [string, string]> = {
      today: [localToday(now), localToday(now)],
      yesterday: [d(y, m, now.getDate() - 1), d(y, m, now.getDate() - 1)],
      this_week: [d(y, m, now.getDate() - ((now.getDay() + 6) % 7)), localToday(now)],
      this_month: [d(y, m, 1), localToday(now)],
      last_month: [d(y, m - 1, 1), d(y, m, 0)],
      this_fy: [d(fyStart, 3, 1), localToday(now)],
    };
    const r = ranges[preset];
    setFromDate(r ? r[0] : '');
    setToDate(r ? r[1] : '');
  };

  const currentUser = useUser();
  const canSeeWholeDirectory = ['superadmin', 'admin', 'department_admin', 'sales_coordinator'].includes(currentUser?.role || '')
    || String(currentUser?.department || '').toLowerCase() === 'accounts';
  const isAccountsUser = String(currentUser?.department || '').toLowerCase() === 'accounts'
    && !['superadmin', 'admin', 'department_admin'].includes(currentUser?.role || '');
  const { data: userOptions = [] } = useUserOptions(canSeeWholeDirectory);
  const isCoordinator = currentUser?.role === 'sales_coordinator';
  const [createdBy, setCreatedBy] = useRememberedState('sales-invoices', 'employee', '');
  const [userSelectedFilter, setUserSelectedFilter] = useState(false);

  useEffect(() => {
    if (isCoordinator && !userSelectedFilter && !createdBy && currentUser?.id) {
      setCreatedBy(currentUser.id);
    }
  }, [isCoordinator, currentUser?.id, userSelectedFilter, createdBy, setCreatedBy]);
  // Client filter (Accounts: one client's invoices for the chosen dates, with
  // the Total Value card following it).
  const [clientFilter, setClientFilter] = useRememberedState('sales-invoices', 'client', '');
  // A remembered date preset means that period TODAY ("This month" moves on).
  useEffect(() => {
    if (datePreset && datePreset !== 'custom') applyPreset(datePreset);
    // once, on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: invoiceClients = [] } = useInvoiceClients();

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [listKind, sort, createdBy, productFilter, fromDate, toDate, clientFilter]);

  // 'All' merges two separately-paged lists. Taking half a page from each
  // lost rows: once one list ran out, later pages of the other were never
  // fetched (client, 2026-10-05: invoices before RBM/26-27/2096 missing from
  // the last page). Instead take the first page*PAGE_SIZE of BOTH (each is
  // sorted the same way server-side), merge, and cut this page out.
  const mergeAll = listKind === 'all';
  const sourceLimit = mergeAll ? Math.min(1000, page * PAGE_SIZE) : PAGE_SIZE;
  const sourcePage = mergeAll ? 1 : page;
  const { data: invoicePage, isLoading } = useInvoiceList({
    // Direct drafts have no client filter of their own; the client's name
    // narrows them the same way (the typed search still wins when present).
    search: searchQuery || clientFilter || undefined,
    page: sourcePage,
    limit: sourceLimit,
    sort,
    createdBy: canSeeWholeDirectory ? createdBy || undefined : undefined,
    productId: productFilter || undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });
  const { data: taxInvoicePage, isLoading: isTaxLoading } = useTaxInvoiceList({
    search: searchQuery || undefined,
    page: sourcePage,
    limit: sourceLimit,
    sort,
    createdBy: canSeeWholeDirectory ? createdBy || undefined : undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    client: clientFilter || undefined,
  });
  const invoices = listKind === 'tax' ? [] : invoicePage?.data ?? [];
  const taxInvoices = listKind === 'draft' ? [] : taxInvoicePage?.data ?? [];
  const draftTotal = invoicePage?.pagination?.total ?? 0;
  const taxTotal = taxInvoicePage?.pagination?.total ?? 0;
  const totalRows = listKind === 'draft' ? draftTotal : listKind === 'tax' ? taxTotal : draftTotal + taxTotal;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const pageStart = totalRows > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(page * PAGE_SIZE, totalRows);

  // Opened from a notification (client, 2026-10-06): ?request=<invoice request>
  // or ?invoice=<tax invoice> opens that invoice straight away, whatever the
  // list's filters are. Consumed once, then stripped from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requestId = searchParams.get('request');
    const invoiceId = searchParams.get('invoice');
    if (!requestId && !invoiceId) return;
    let cancelled = false;
    (async () => {
      let t: TaxInvoiceRow | undefined = taxInvoices.find((r) => (requestId && r._id === requestId) || (invoiceId && r.invoiceId === invoiceId));
      if (!t && requestId) {
        try {
          const { data } = await api.get<TaxInvoiceRow & { id?: string }>(`/api/invoice-requests/${encodeURIComponent(requestId)}`);
          t = { ...data, _id: data._id || data.id || requestId };
        } catch { /* not found or not theirs — just show the list */ }
      }
      if (cancelled) return;
      const previewId = t?.invoiceId || invoiceId;
      if (previewId) {
        setPreviewRow({
          kind: 'tax',
          key: t?._id || previewId,
          number: t ? (t.finalized ? (t.invoiceNumber || t.number) : `${t.number} (draft)`) : 'Invoice',
          date: (t?.finalized && (t.invoiceDate || t.date)) ? (t.invoiceDate || t.date || '') : (t?.requestedAt || ''),
          clientName: t?.clientName || '',
          itemCount: t?.items?.length || 0,
          total: t?.total || 0,
          status: t?.finalized === false ? 'draft' : 'invoiced',
          previewId,
          tax: t,
          finalized: t?.finalized,
        });
      } else if (t?.number) {
        // Requested but no invoice generated yet — find it in the list.
        setSearchInput(t.number);
        setSearchQuery(t.number);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('request');
      next.delete('invoice');
      setSearchParams(next, { replace: true });
    })();
    return () => { cancelled = true; };
    // Only the link parameters drive this; the list is a shortcut when loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Everything the Invoices menu can show, in one list: direct-sale drafts
  // (typed up here, not yet a tax invoice) alongside real, ledger-posted Tax
  // Invoices generated elsewhere (Quotation/Proforma → "Create Tax Invoice").
  const rows = useMemo<InvoiceRow[]>(() => {
    const draftRows: InvoiceRow[] = invoices.map((i) => ({
      kind: 'draft',
      key: i._id,
      number: i.orderNumber || i.documentNumber || i._id.slice(-6),
      date: i.date,
      clientName: i.customerName || 'No client',
      itemCount: i.lines?.length || 0,
      total: i.grandTotal || 0,
      status: i.status,
      previewId: i._id,
      draft: i,
      dispatchStatus: i.dispatchStatus,
    }));
    const taxRows: InvoiceRow[] = taxInvoices.map((t) => ({
      kind: 'tax',
      key: t._id,
      number: t.finalized ? (t.invoiceNumber || t.number) : `${t.number} (draft)`,
      date: (t.finalized && (t.invoiceDate || t.date)) ? (t.invoiceDate || t.date || '') : t.requestedAt,
      clientName: t.clientName || 'No client',
      itemCount: t.items?.length || 0,
      total: t.total || 0,
      status: t.finalized ? 'invoiced' : 'draft',
      previewId: t.invoiceId,
      tax: t,
      finalized: t.finalized,
      dispatchStatus: t.dispatchStatus,
    }));
    const merged = [...draftRows, ...taxRows].sort(compareRows(sort));
    return mergeAll ? merged.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : merged;
  }, [invoices, taxInvoices, sort, mergeAll, page]);

  const totalValue = (listKind === 'tax'
    ? taxInvoicePage?.stats?.totalValue
    : listKind === 'draft'
      ? invoicePage?.stats?.totalValue
      : (invoicePage?.stats?.totalValue || 0) + (taxInvoicePage?.stats?.totalValue || 0)) || 0;
  const openCount = invoicePage?.stats?.openCount ?? 0;
  const taxStats = listKind === 'draft' ? undefined : taxInvoicePage?.stats;
  const billCount = taxStats?.billCount ?? 0;
  // "RBM/26-27/2000 – 2009" within one series; both in full when the series differ.
  const seriesOf = (n?: string) => String(n || '').split('/').slice(0, -1).join('/');
  const shortBill = (n?: string) => (seriesOf(n) === seriesOf(taxStats?.firstBillNo) ? String(n || '').split('/').pop() || '' : String(n || ''));
  const billRange = taxStats?.firstBillNo
    ? (taxStats.firstBillNo === taxStats.lastBillNo ? taxStats.firstBillNo : `${taxStats.firstBillNo} – ${shortBill(taxStats.lastBillNo)}`)
    : '';
  const fmtDay = (v: string) => new Date(v + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const rangeLabel = fromDate || toDate
    ? (fromDate === toDate ? fmtDay(fromDate) : `${fromDate ? fmtDay(fromDate) : 'start'} – ${toDate ? fmtDay(toDate) : 'today'}`)
    : '';
  const { data: productData } = useProducts();
  const { data: dealsData } = useDeals();
  const { create, update, updateTaxInvoice, discardDraftInvoice, discardDirectSaleDraft } = useInvoiceMutations();
  const sendInvoice = useSendTaxInvoice();
  const { finalizeTaxInvoice } = useDocumentChain();

  const handleDiscardDraft = async (row: InvoiceRow) => {
    const isTax = row.kind === 'tax';
    const label = row.number;
    if (!window.confirm(`Are you sure you want to discard draft invoice ${label}? This will remove the unfinalized invoice and allow the source order to be re-invoiced.`)) {
      return;
    }
    try {
      if (isTax) {
        const id = row.tax?.invoiceId || row.previewId || row.tax?._id || row.key;
        if (!id) return;
        await discardDraftInvoice.mutateAsync(id);
        toast.success(`Draft invoice ${label} discarded successfully.`);
      } else {
        const id = row.draft?._id || row.previewId || row.key;
        if (!id) return;
        await discardDirectSaleDraft.mutateAsync(id);
        toast.success(`Draft ${label} discarded.`);
      }
      if (previewRow?.key === row.key || previewRow?.previewId === row.previewId || previewRow?.number === row.number) {
        setPreviewRow(null);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to discard draft invoice.');
    }
  };

  const handleSendInvoice = (invoiceId: string) => {
    sendInvoice.mutate(invoiceId, {
      onSuccess: (data) => {
        if (data.ok) toast.success(describeSendResult(data.result));
        else toast.error(describeSendResult(data.result));
      },
      onError: (err) => toast.error(sendErrorText(err, 'Could not send the invoice.')),
    });
  };

  const [preparingDispatchId, setPreparingDispatchId] = useState<string | null>(null);
  const qc = useQueryClient();

  const handlePrepareDispatch = async (row: InvoiceRow) => {
    const invId = row.tax?.invoiceId || row.previewId;
    if (!invId) return;
    setPreparingDispatchId(invId);
    try {
      const endpoint = row.tax?._id
        ? `/api/invoice-requests/${row.tax._id}/dispatch-order`
        : `/api/accounts/sales-invoices/${invId}/dispatch-order`;
      await api.post(endpoint);
      await qc.invalidateQueries({ queryKey: ['invoice-requests'] });
      toast.success('Dispatch order prepared.');
    } catch (err) {
      toast.error(errorText(err, 'Could not prepare dispatch order.'));
    } finally {
      setPreparingDispatchId(null);
    }
  };

  // Finalize & Post a draft tax invoice — allocates the GST number, posts the
  // ledger, hands the order to Dispatch. The advance-payment gate bites here:
  // a 422 (nobody may override, or the caller doesn't hold that permission)
  // is shown as-is; a 400 OVERRIDE_REASON_REQUIRED means the caller CAN
  // finalize anyway but must record why, so the dialog re-opens with a
  // reason box instead of just an error toast. Keyed by row so the dialog
  // opens against the right one.
  const [financeBlock, setFinanceBlock] = useState<{ invoiceId: string; label: string; blocked: AdvanceBlocked } | null>(null);
  const [financeReason, setFinanceReason] = useState('');
  const handleFinalize = (invoiceId: string, label: string, overrideReason?: string) => {
    finalizeTaxInvoice.mutate({ invoiceId, overrideReason }, {
      onSuccess: (data) => {
        setFinanceBlock(null);
        setFinanceReason('');
        toast.success(data.alreadyFinalized ? 'Already finalized.' : `Invoice ${data.invoiceNumber || ''} finalized and posted.`);
      },
      onError: (err) => {
        const advance = asAdvanceBlocked(err) || asOverrideReasonRequired(err);
        if (advance) { setFinanceBlock({ invoiceId, label, blocked: advance }); return; }
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || 'Could not finalize the invoice.');
      },
    });
  };

  // Cancel & Zero Invoice (when a finalized order is cancelled by the client):
  // the Accounts team only, plus the system admins — never a BDM, a Sales
  // manager / department admin or the Sales Coordinator (client, 2026-10-23).
  // Same rule as the server's requireInvoiceCancelUser.
  const canCancelInvoice =
    ['superadmin', 'admin', 'owner'].includes(String(currentUser?.role || '').toLowerCase()) ||
    String(currentUser?.department || '').toLowerCase() === 'accounts';
  const [cancelTarget, setCancelTarget] = useState<InvoiceRow | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false);

  const handleCancelInvoice = async () => {
    if (!cancelTarget) return;
    const reason = cancellationReason.trim();
    if (!reason) {
      toast.error('Please enter a cancellation reason');
      return;
    }
    setIsCancellingInvoice(true);
    try {
      const invoiceId = cancelTarget.tax?.invoiceId || cancelTarget.previewId || cancelTarget.key;
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoiceId}/cancel`, { reason });
      toast.success(`Invoice ${cancelTarget.number} cancelled and zeroed.`);
      // What happened to its dispatch order — withdrawn, or the goods had already left.
      const dispatch = data?.dispatch as { withdrawn?: string[]; alreadyDispatched?: string[] } | undefined;
      if (dispatch?.withdrawn?.length) toast(`Removed from Dispatch: ${dispatch.withdrawn.join(', ')} (its challan cancelled too).`, { icon: '🚚', duration: 7000 });
      if (dispatch?.alreadyDispatched?.length) toast.error(`Goods for ${dispatch.alreadyDispatched.join(', ')} were already dispatched — arrange the return / credit note.`, { duration: 10000 });
      setCancelTarget(null);
      setCancellationReason('');
      qc.invalidateQueries({ queryKey: ['invoice-requests'] });
      qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to cancel invoice');
    } finally {
      setIsCancellingInvoice(false);
    }
  };

  const closedWonClients = useMemo(() => {
    if (!dealsData?.deals) return [];
    return dealsData.deals
      .filter((d) => d.stage === 'closed_won')
      .map((d) => {
        const name = d.contact_name || d.business_name || d.title;
        return { label: name, value: name };
      })
      .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i);
  }, [dealsData]);

  const products = useMemo(
    () => (productData?.data ?? []) as unknown as Array<{
      _id: string; name: string; sellingPrice?: number; hsnSac?: string;
      cgstRate?: number; sgstRate?: number; igstRate?: number;
      packagingType?: string; packSize?: string; qtyPerPack?: number; totalPackSize?: string; volumeLiters?: number;
    }>,
    [productData],
  );

  const productOptions = useMemo(
    () => products
      .map((p) => ({ value: p._id, label: p.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [products],
  );

  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const pickProduct = (key: string, productId: string) => {
    const p = products.find((x) => x._id === productId);
    setLine(key, {
      productId,
      name: p?.name || '',
      description: p?.name || '',
      unitPrice: p?.sellingPrice ?? 0,
      unit: abbreviatePackaging(derivePackagingType(p)) || '',
      packSize: p?.packSize || '',
      qtyPerPack: Number(p?.qtyPerPack) || 0,
      totalPackSize: p?.totalPackSize || '',
      volumeLiters: Number(p?.volumeLiters) || 0,
      cgstRate: p?.cgstRate ?? 0,
      sgstRate: p?.sgstRate ?? 0,
      igstRate: p?.igstRate ?? 0,
    });
  };

  const lineNet = (l: DraftLine) => {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
    const disc = l.discountType === 'percent'
      ? gross * ((Number(l.discount) || 0) / 100)
      : Number(l.discount) || 0;
    return Math.max(0, gross - disc);
  };
  const draftSubtotal = lines.reduce((s, l) => s + lineNet(l), 0);
  const draftTax = lines.reduce(
    (s, l) => s + lineNet(l) * (((l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0)) / 100), 0);
  const canSubmit = Boolean(customerName.trim()) && lines.some(
    (line) => Boolean(line.description?.trim()) && Number(line.quantity) > 0,
  );

  const resetForm = () => {
    setCustomerName(''); setCustomerEmail(''); setNotes(''); setLines([emptyLine()]);
    setEditingId(null);
    setEditingTaxInvoice(false);
  };

  // Arriving from the profile page's "Edit" button (?edit=<id>).
  const editParamId = new URLSearchParams(window.location.search).get('edit');
  useEffect(() => {
    if (!editParamId || isFormOpen) return;
    const match = invoices.find((i) => i._id === editParamId);
    if (match) navigate(`/invoices/${match._id}/edit`, { replace: true });
  }, [editParamId, invoices, isFormOpen]);

  const submit = () => {
    if (isAccountsUser) {
      toast.error('Accounts users cannot edit invoices. Please contact your administrator.');
      return;
    }
    if (!customerName.trim()) return toast.error('Client name is required.');
    const usable = lines.filter((l) => (l.description || '').trim() && Number(l.quantity) > 0);
    if (usable.length === 0) return toast.error('Add at least one line with a description and quantity.');

    const payload = {
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      notes: notes.trim(),
      lines: usable.map(({ key: _key, name: _name, ...l }) => l),
    };

    if (editingId && editingTaxInvoice) {
      updateTaxInvoice.mutate(
        {
          id: editingId,
          payload: {
            notes: notes.trim(),
            lineItems: usable.map(({ key: _key, name: _name, unitPrice, ...l }) => ({
              productId: l.productId || null,
              description: l.description || _name || '',
              hsnSac: l.hsnSac || '',
              unit: l.unit || '',
              packSize: l.packSize || '',
              qtyPerPack: Number(l.qtyPerPack) || 0,
              totalPackSize: l.totalPackSize || '',
              volumeLiters: Number(l.volumeLiters) || 0,
              quantity: Number(l.quantity) || 1,
              rate: Number(unitPrice) || 0,
              discount: l.discountType === 'percent'
                ? ((Number(l.quantity) || 0) * (Number(unitPrice) || 0) * (Number(l.discount) || 0)) / 100
                : Number(l.discount) || 0,
              cgstRate: Number(l.cgstRate) || 0,
              sgstRate: Number(l.sgstRate) || 0,
              igstRate: Number(l.igstRate) || 0,
            })),
          },
        },
        {
          onSuccess: () => { toast.success('Final invoice updated. Dispatch and Accounts now see the latest copy.'); resetForm(); },
          onError: (err) => toast.error(errorText(err, 'Could not update the final invoice.')),
        },
      );
    } else if (editingId) {
      update.mutate(
        { id: editingId, payload },
        {
          onSuccess: () => { toast.success('Invoice updated.'); setEditingId(null); resetForm(); },
          onError: (err) => toast.error(errorText(err, 'Could not update the invoice.')),
        },
      );
    } else {
      create.mutate(
        payload,
        {
          onSuccess: () => { toast.success('Invoice created.'); setCreating(false); resetForm(); },
          onError: (err) => toast.error(errorText(err, 'Could not create the invoice.')),
        },
      );
    }
  };

  if (isLoading || isTaxLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={ReceiptText}
        eyebrow="Sales"
        title="Invoices"
        description="Tax invoices raised from your quotations and proformas."
      />

      <StatGrid cols={4}>
        <StatCard label="Total Invoices" value={totalRows} hint={rangeLabel ? `${rangeLabel} · ${billCount} bill${billCount === 1 ? '' : 's'}` : 'Direct-sale drafts + tax invoices'} icon={Receipt} tone="primary" />
        <StatCard label="Invoice Value" value={formatINR(totalValue)} hint={rangeLabel || clientFilter ? `Total for ${[clientFilter, rangeLabel].filter(Boolean).join(' · ')}` : 'Across all invoices'} icon={Wallet} tone="info" />
        <StatCard label="Finalized Bills" value={billCount} hint={billRange ? `No. ${billRange}` : 'No finalized bills in this view'} icon={Receipt} tone="primary" />
        <StatCard label="Awaiting Request" value={openCount} hint="Drafts not yet sent to Accounts" icon={Clock} tone="warning" />
      </StatGrid>

      {isFormOpen && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              placeholder="Select Client *"
              value={customerName}
              options={closedWonClients}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <Input placeholder="Client email" value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)} />
          </div>

          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} className="grid gap-2 sm:grid-cols-12 items-center">
                <div className="sm:col-span-4 space-y-1">
                  <Input
                    placeholder="Item / description *"
                    value={l.description || ''}
                    onChange={(e) => !isAccountsUser && setLine(l.key, { description: e.target.value })}
                    disabled={isAccountsUser}
                  />
                  {productOptions.length > 0 && (
                    <Select
                      value={l.productId ?? ''}
                      placeholder="…or pick a catalog item"
                      options={productOptions}
                      onChange={(e) => !isAccountsUser && pickProduct(l.key, e.target.value)}
                      disabled={isAccountsUser}
                    />
                  )}
                  {packSummary(l) && (
                    <p className="text-data-xs font-semibold text-success-text">{packSummary(l)}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Input type="number" min={1} placeholder="Qty"
                    value={l.quantity}
                    onChange={(e) => !isAccountsUser && setLine(l.key, { quantity: Number(e.target.value) })}
                    disabled={isAccountsUser}
                  />
                  {totalBaseQuantityLabel(l.quantity, l) && (
                    <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                      Total {totalBaseQuantityLabel(l.quantity, l)}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  {(() => {
                    const product = products.find((p) => p._id === l.productId) || l;
                    const baseUnit = baseUnitDivisor(product);
                    const packPieces = piecesPerPack(product);
                    const hasPieces = packPieces > 1;
                    const mode = (baseUnit || hasPieces) ? (rateModeByKey[l.key] || 'container') : 'container';
                    const divisor = mode === 'base' ? baseUnit!.qty : mode === 'piece' ? packPieces : 1;
                    const enteredRate = (Number(l.unitPrice) || 0) / divisor;
                    return <>
                      <Input type="number" min={0}
                        placeholder={mode === 'base' ? `Rate / ${baseUnit!.unit}` : 'Rate / container'}
                        value={enteredRate}
                        onChange={(e) => {
                          if (isAccountsUser) return;
                          const rate = Number(e.target.value) || 0;
                          setLine(l.key, { unitPrice: rate * divisor });
                        }}
                        disabled={isAccountsUser}
                      />
                      {(baseUnit || hasPieces) && (
                        <select
                          value={mode}
                          onChange={(e) => setRateModeByKey((current) => ({ ...current, [l.key]: e.target.value as 'container' | 'base' }))}
                          className="mt-1 w-full rounded border-0 bg-transparent text-data-xs font-medium text-on-surface-variant focus:outline-none"
                          aria-label="Rate entry unit"
                        >
                          <option value="container">per container{baseUnit ? ` (${baseUnit.qty} ${baseUnit.unit})` : ''}</option>
                          {hasPieces && <option value="piece">per piece{l.packSize ? ` (${l.packSize})` : ''}</option>}
                          {baseUnit && <option value="base">per {baseUnit.unit}</option>}
                        </select>
                      )}
                    </>;
                  })()}
                  <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                    {ratePerBaseUnit(l.unitPrice, l)}
                  </p>
                </div>
                <div className="sm:col-span-2 flex gap-1">
                  <Select
                    value={l.discountType}
                    options={[{ value: 'percent', label: '%' }, { value: 'fixed', label: '₹' }]}
                    onChange={(e) => !isAccountsUser && setLine(l.key, { discountType: e.target.value as 'percent' | 'fixed', discount: 0 })}
                    aria-label="Discount type"
                    className="w-14 shrink-0"
                    disabled={isAccountsUser}
                  />
                  <Input type="number" min={0} max={l.discountType === 'percent' ? 100 : undefined}
                    placeholder={l.discountType === 'percent' ? 'Disc %' : 'Discount ₹'}
                    value={l.discount}
                    onChange={(e) => !isAccountsUser && setLine(l.key, { discount: Number(e.target.value) || 0 })}
                    disabled={isAccountsUser}
                  />
                </div>
                <div className="sm:col-span-1 text-right text-sm font-medium">
                  {formatINR(lineNet(l))}
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <Button variant="ghost" onClick={() => setLines((ls) =>
                    ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {!isAccountsUser && (
              <Button variant="secondary" onClick={() => setLines((ls) => [...ls, emptyLine()])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            )}
          </div>

          <Input placeholder="Notes" value={notes} onChange={(e) => !isAccountsUser && setNotes(e.target.value)} disabled={isAccountsUser} />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Net </span>
              <strong>{formatINR(draftSubtotal)}</strong>
              <span className="text-muted-foreground"> + tax </span>
              <strong>{formatINR(draftTax)}</strong>
              <span className="text-muted-foreground"> = </span>
              <strong>{formatINR(draftSubtotal + draftTax)}</strong>
            </div>
            <Button onClick={submit} loading={create.isPending || update.isPending || updateTaxInvoice.isPending} disabled={!canSubmit || isAccountsUser}>
              {editingTaxInvoice ? 'Update final invoice' : editingId ? 'Update invoice' : 'Save invoice'}
            </Button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="flex flex-col gap-3 rounded-card border border-outline bg-surface p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 text-on-surface-subtle absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search number, client name, quotation..."
            className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-outline bg-surface-container-low text-on-surface placeholder-on-surface-variant focus:outline-primary transition"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[
            ['all', 'All'],
            ['draft', 'Direct Drafts'],
            ['tax', 'Tax Invoices'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setListKind(value as 'all' | 'draft' | 'tax')}
              className={cn(
                'rounded-lg border px-3 py-2 text-xs font-bold transition-colors',
                listKind === value ? 'border-primary bg-primary/10 text-primary' : 'border-outline text-on-surface-variant hover:bg-surface-container-low',
              )}
            >
              {label}
            </button>
          ))}
          <div className="w-44">
            <Select
              name="invoices-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as InvoiceSort)}
              options={SORT_OPTIONS}
            />
          </div>
          {/* Whole-directory roles only — a scoped user's own list is already
              restricted to themself, so this would be a dropdown of one. */}
          {canSeeWholeDirectory && (
            <div className="w-56">
              <Select
                name="invoices-created-by-filter"
                value={createdBy}
                onChange={(e) => {
                  setUserSelectedFilter(true);
                  setCreatedBy(e.target.value);
                }}
                options={[
                  { value: '', label: 'All employees' },
                  ...(currentUser?.id ? [{ value: currentUser.id, label: `My Invoices (${currentUser.displayName || currentUser.username})` }] : []),
                  ...userOptions
                    .filter((u) => u.id !== currentUser?.id)
                    .map((u) => ({ value: u.id, label: u.display_name })),
                ]}
              />
            </div>
          )}
          <div className="w-60">
            <SearchableSelect
              name="invoices-client-filter"
              value={clientFilter}
              onChange={setClientFilter}
              placeholder="All clients"
              searchPlaceholder="Search client…"
              emptyText="No client found"
              clearable
              options={invoiceClients.map((c) => ({ value: c, label: c }))}
            />
          </div>
          {/* Which product sells most, seen from Direct Invoices — matches
              the same lines.productId the "Product demand" reports (Reports
              page) already aggregate on. Only narrows Direct Drafts rows;
              Tax Invoices come from a separate source with no product
              filter yet, so switch to the "Direct Drafts" tab to see this
              filter's full effect. */}
          <div className="w-56">
            <Select
              name="invoices-product-filter"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              options={[
                { value: '', label: 'All products' },
                ...productOptions,
              ]}
            />
          </div>
          <div className="w-40">
            <Select
              name="invoices-date-preset"
              value={datePreset}
              onChange={(e) => applyPreset(e.target.value)}
              options={[
                { value: '', label: 'All dates' },
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'this_week', label: 'This week' },
                { value: 'this_month', label: 'This month' },
                { value: 'last_month', label: 'Last month' },
                { value: 'this_fy', label: 'This financial year' },
                ...(datePreset === 'custom' ? [{ value: 'custom', label: 'Custom range' }] : []),
              ]}
            />
          </div>
          <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
            From
            <input type="date" aria-label="From date" value={fromDate} max={toDate || undefined}
              onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
              className="h-10 rounded-lg border border-outline bg-surface px-2 text-body-sm text-on-surface" />
          </label>
          <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
            To
            <input type="date" aria-label="To date" value={toDate} min={fromDate || undefined}
              onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
              className="h-10 rounded-lg border border-outline bg-surface px-2 text-body-sm text-on-surface" />
          </label>
          {(fromDate || toDate) && (
            <button type="button" onClick={() => applyPreset('')}
              className="rounded-lg border border-outline px-3 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-container-low">
              Clear dates
            </button>
          )}
          {(searchInput.trim() || listKind !== 'all' || sort !== 'date_desc' || createdBy || clientFilter || productFilter || fromDate || toDate) && (
            <button type="button"
              onClick={() => { setSearchInput(''); setSearchQuery(''); setListKind('all'); setSort('date_desc'); setCreatedBy(''); setClientFilter(''); setProductFilter(''); applyPreset(''); }}
              className="rounded-lg border border-outline px-3 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-container-low">
              Reset filters
            </button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No invoices yet"
          description="Type up a direct sale here, or create a tax invoice from a Quotation or Proforma — both show up in this list."
        />
      ) : (
        <div className="space-y-3">
          <div className="overflow-hidden rounded-card border border-outline bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left text-body-md">
                <thead className="border-b border-outline bg-surface-container-lowest">
                  <tr className="text-body-xs font-semibold uppercase tracking-wider text-on-surface-subtle">
                    <th scope="col" className="px-4 py-3">Invoice</th>
                    <th scope="col" className="px-4 py-3">Status</th>
                    <th scope="col" className="px-4 py-3">Dispatch</th>
                    <th scope="col" className="px-4 py-3">Client</th>
                    <th scope="col" className="px-4 py-3 text-right">Items</th>
                    <th scope="col" className="px-4 py-3">Date</th>
                    <th scope="col" className="px-4 py-3 text-right">Amount</th>
                    {/* Pinned to the right edge so Finalize & Post / Hand off stay on screen at any zoom. */}
                    <th scope="col" className="sticky right-0 z-10 bg-surface-container-lowest px-4 py-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline">
          {rows.map((r) => {
            const isDraft = r.kind === 'draft';
            const isEditableDraft = isDraft && r.status === 'draft';
            const isUnfinalizedTax = r.kind === 'tax' && !r.finalized;
            // A withdrawn invoice stays listed for the record, with no actions.
            const isCancelledTax = r.kind === 'tax' && ['cancelled', 'voided', 'reversed'].includes(r.tax?.invoiceStatus || '');
            const isPendingApproval = r.kind === 'tax' && (r.tax?.status === 'requested' || (r.tax?.needsApproval && !r.finalized) || !r.previewId);
            const onOpen = () => (isDraft ? navigate(`/invoices/${r.key}`) : isPendingApproval ? undefined : setPreviewRow(r));
            return (
              <tr
                key={r.key}
                tabIndex={0}
                onClick={onOpen}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }}
                aria-label={`Open ${r.number}`}
                className="group cursor-pointer transition-colors hover:bg-surface-container-low focus-visible:bg-surface-container-low focus-visible:outline-none"
              >
                <td className="whitespace-nowrap px-4 py-3">
                  <span className="font-mono text-body-sm font-semibold text-primary">{r.number}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <Badge className={cn('text-data-xs font-bold uppercase', isCancelledTax ? 'bg-danger/10 text-danger-text' : isDraft ? (isEditableDraft ? 'bg-warning/10 text-warning-text' : 'bg-success/10 text-success-text') : isPendingApproval ? 'bg-warning/10 text-warning-text' : isUnfinalizedTax ? 'bg-warning/10 text-warning-text' : 'bg-success/10 text-success-text')}>
                    {isCancelledTax ? r.tax!.invoiceStatus : isDraft ? r.status : isPendingApproval ? 'Pending Approval' : isUnfinalizedTax ? 'Draft — not posted' : 'Tax Invoice'}
                  </Badge>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {renderDispatchBadge(r.dispatchStatus)}
                </td>
                <td className="max-w-[260px] truncate px-4 py-3 font-medium text-on-surface">{r.clientName}</td>
                <td className="px-4 py-3 text-right tabular-nums text-on-surface-variant">{r.itemCount}</td>
                <td className="whitespace-nowrap px-4 py-3 text-on-surface-variant">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-on-surface">
                  {isCancelledTax ? (
                    <div className="flex flex-col items-end">
                      <span className="font-mono text-danger-text font-bold">₹0</span>
                      <span className="line-through text-xs text-muted-foreground">{formatINR(r.total)}</span>
                    </div>
                  ) : (
                    formatINR(r.total)
                  )}
                </td>
                <td className="sticky right-0 z-[1] w-[1%] bg-surface px-4 py-3 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)] group-hover:bg-surface-container-low" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <div className="ml-auto flex items-center justify-end gap-1.5 whitespace-nowrap">
                  {isPendingApproval && (
                    <span className="text-xs text-warning-text font-medium italic pr-2">Awaiting Approval</span>
                  )}
                  {!isPendingApproval && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPreviewRow(r)}
                        title="View document"
                        aria-label={`View document ${r.number}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-info/30 text-info-text hover:bg-info/10 transition shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={getDocUrl(r, 'pdf')}
                        target="_blank"
                        rel="noreferrer"
                        title="Download PDF"
                        aria-label={`Download PDF ${r.number}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-muted transition shrink-0"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    </>
                  )}
                  {isDraft && !['cancelled', 'expired', 'superseded'].includes(r.status) && r.draft && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/${r.draft!._id}/edit`)} className="gap-1" title="Edit draft" aria-label={`Edit draft ${r.number}`}>
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      {isEditableDraft && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDiscardDraft(r)}
                          className="gap-1 border-danger/30 text-danger-text hover:bg-danger/10 hover:border-danger/30"
                          title="Discard draft invoice"
                          aria-label={`Discard ${r.number}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="sr-only">Discard</span>
                        </Button>
                      )}
                    </>
                  )}
                  {!isDraft && isUnfinalizedTax && r.tax && !isPendingApproval && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/tax/${r.tax!.invoiceId}/edit`)} className="gap-1" title="Edit invoice" aria-label={`Edit ${r.number}`}>
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDiscardDraft(r)}
                        className="gap-1 border-danger/30 text-danger-text hover:bg-danger/10 hover:border-danger/30"
                        title="Discard draft invoice"
                        aria-label={`Discard ${r.number}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span className="sr-only">Discard</span>
                      </Button>
                      <Button size="sm" loading={finalizeTaxInvoice.isPending} onClick={() => handleFinalize(r.previewId, r.number)} className="gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Finalize & Post</span>
                      </Button>
                    </>
                  )}
                  {!isDraft && !isUnfinalizedTax && !isCancelledTax && !isPendingApproval && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/tax/${r.tax!.invoiceId}/edit`)} className="gap-1" title="Edit invoice" aria-label={`Edit ${r.number}`}>
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      {/* "Hand off to Dispatch" is the single action for this —
                          its own form asks "Create Delivery Challan? Yes/No"
                          (DocumentActions/EXTRA_INPUT_TARGETS.delivery_challan),
                          so a separate "Issue Delivery Challan" button here used
                          to duplicate that same choice as a second control. */}
                      {r.tax!.salesOrderId ? (
                        <DocumentActions docType="sales_order" docId={r.tax!.salesOrderId} size="sm" />
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          loading={preparingDispatchId === (r.tax?.invoiceId || r.previewId)}
                          onClick={() => handlePrepareDispatch(r)}
                          className="gap-1"
                        >
                          <span>Hand off to Dispatch</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" loading={sendInvoice.isPending} onClick={() => handleSendInvoice(r.previewId)} className="gap-1" title="Send invoice to client" aria-label={`Send ${r.number} to client`}>
                        <Send className="w-3.5 h-3.5" />
                        <span className="sr-only">Send</span>
                      </Button>
                      {canCancelInvoice && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCancelTarget(r);
                            setCancellationReason('');
                          }}
                          className="gap-1 border-danger/30 text-danger-text hover:bg-danger/10 hover:border-danger/30"
                          title="Cancel & zero finalized invoice"
                          aria-label={`Cancel and zero ${r.number}`}
                        >
                          <Ban className="w-3.5 h-3.5" />
                          <span>Cancel & Zero</span>
                        </Button>
                      )}
                    </>
                  )}
                  {isDraft && (
                    <DocumentChainActions
                      documentId={r.key}
                      documentType="direct_sale"
                      documentNumber={r.number}
                      disabled={!isEditableDraft}
                    />
                  )}
                </div>
                </td>
              </tr>
            );
          })}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-muted-foreground">
                Showing {pageStart}-{pageEnd} of {totalRows} · {SORT_OPTIONS.find((o) => o.value === sort)?.label ?? 'Newest first'}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1 || isLoading || isTaxLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages || isLoading || isTaxLoading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}



      {previewRow && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-inverse-surface/60 backdrop-blur-xs p-3 md:p-6 animate-in fade-in duration-200">
          <div className="bg-card rounded-card border border-border shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-muted">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-info/10 text-info-text flex items-center justify-center font-black">
                  <Receipt className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-on-surface">
                    Invoice: {previewRow.number}
                  </h3>
                  {previewRow.clientName && (
                    <p className="text-data-xs text-on-surface-variant font-medium">
                      {previewRow.clientName} · Total: {formatINR(previewRow.total)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const editUrl = previewRow.kind === 'tax'
                      ? `/invoices/tax/${previewRow.tax?.invoiceId || previewRow.previewId}/edit`
                      : `/invoices/${previewRow.draft?._id || previewRow.previewId}/edit`;
                    setPreviewRow(null);
                    navigate(editUrl);
                  }}
                  className="rounded-xl text-xs font-bold flex items-center gap-1.5 border border-border shadow-2xs"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  <span>{previewRow.finalized === false || previewRow.status === 'draft' ? 'Edit Draft' : 'Edit Invoice'}</span>
                </Button>
                {((previewRow.kind === 'tax' && !previewRow.finalized) || (previewRow.kind === 'draft' && previewRow.status === 'draft')) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDiscardDraft(previewRow)}
                    className="rounded-xl text-xs font-bold flex items-center gap-1.5 border-danger/30 text-danger-text hover:bg-danger/10 shadow-2xs"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Discard Draft</span>
                  </Button>
                )}
                <a
                  href={getDocUrl(previewRow, 'pdf')}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-primary hover:bg-primary-dim text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download PDF</span>
                </a>
                <a
                  href={getDocUrl(previewRow)}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-xl border border-border bg-card text-on-surface-variant hover:bg-muted text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Open in Tab</span>
                </a>
                <button
                  onClick={() => setPreviewRow(null)}
                  className="p-1.5 rounded-xl text-on-surface-subtle hover:text-on-surface-variant hover:bg-muted transition ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-muted p-2 md:p-4 overflow-hidden">
              <iframe
                src={getDocUrl(previewRow)}
                className="w-full h-full rounded-xl bg-card border border-border shadow-inner"
                title={`Invoice Preview ${previewRow.key}`}
              />
            </div>
          </div>
        </div>
      )}

      {financeBlock && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-inverse-surface/60 p-4">
          <div className="w-full max-w-md rounded-card border border-border bg-card p-5 shadow-xl">
            <p className="font-semibold text-sm">Payment required before posting</p>
            <p className="mt-1 text-xs text-muted-foreground">
              This customer&rsquo;s terms require {formatINR(financeBlock.blocked.requiredAdvance)} in advance.{' '}
              {formatINR(financeBlock.blocked.shortfall)} is still outstanding on {financeBlock.label}.
            </p>
            {financeBlock.blocked.overridable ? (
              <div className="mt-3 flex flex-col gap-2">
                <label className="text-xs font-medium">To finalize anyway, record why. This is written onto the invoice.</label>
                <input
                  value={financeReason}
                  onChange={(e) => setFinanceReason(e.target.value)}
                  placeholder="e.g. RTGS in flight, confirmed by accounts"
                  className="rounded border px-2 py-1 text-sm"
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => { setFinanceBlock(null); setFinanceReason(''); }}>Cancel</Button>
                  <Button
                    disabled={!financeReason.trim() || finalizeTaxInvoice.isPending}
                    onClick={() => handleFinalize(financeBlock.invoiceId, financeBlock.label, financeReason.trim())}
                  >
                    Finalize anyway
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" onClick={() => setFinanceBlock(null)}>Close</Button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Cancel & Zero Invoice Modal */}
      <Modal
        open={!!cancelTarget}
        onClose={() => {
          if (!isCancellingInvoice) {
            setCancelTarget(null);
            setCancellationReason('');
          }
        }}
        title={`Cancel & Zero Invoice ${cancelTarget?.number || ''}`}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setCancelTarget(null);
                setCancellationReason('');
              }}
              disabled={isCancellingInvoice}
            >
              No, Keep Invoice
            </Button>
            <Button
              variant="primary"
              onClick={handleCancelInvoice}
              disabled={isCancellingInvoice || !cancellationReason.trim()}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isCancellingInvoice ? 'Cancelling & Zeroing...' : 'Yes, Cancel & Zero Invoice'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-card-sm border border-danger/25 bg-danger/10 p-3 text-xs text-danger-text space-y-1">
            <p className="font-semibold">⚠️ Attention Accounts Team</p>
            <p>
              This client order was cancelled. Cancelling will post a reversal (credit note) entry to the ledger and mark this invoice balance as <strong>₹0 (Zero)</strong>.
            </p>
            <p className="text-muted-foreground">
              This action cannot be undone. Any linked draft delivery challans will also be cancelled.
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1 text-on-surface">
              Reason for Cancellation <span className="text-danger">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="e.g. Client cancelled order after finalization, payment not received"
              className="w-full rounded-md border border-outline px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
